import { useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useLang } from "../../../shared/i18n/useLang";
import { buscarCasas, temporalesHoy, texto as textoDe, textoMotivo } from "../services/seguridadService";

const ICONO = { PROPIETARIO: "fa-house-user", COPROPIETARIO: "fa-user-group", AUTORIZADO: "fa-user-check" };

/**
 * Pantalla principal del guarda: una sola barra que busca por casa, nombre,
 * cédula o placa, y muestra cada casa con su gente, teléfonos y placas. Desde
 * acá se registra el ingreso de alguien de la casa o de una visita.
 *
 * No lista casas mientras no se escriba nada: en un condominio con cientos de
 * filiales esa lista no le sirve al guarda. Muestra como mucho 30 resultados
 * (primero la casa exacta) y avisa si hay más para que afine la búsqueda.
 */
export default function GaritaBuscar() {
  const navigate = useNavigate();
  const L = useLang();
  const { adentro } = useOutletContext();
  const [texto, setTexto] = useState("");
  const [casas, setCasas] = useState([]);
  const [total, setTotal] = useState(0);
  const [hayMas, setHayMas] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const pedido = useRef(0);
  const [temporales, setTemporales] = useState([]);

  // Visitas que los residentes dejaron autorizadas para hoy: se ven apenas se
  // abre la pantalla y se refrescan solas (un residente puede agregar una en
  // cualquier momento).
  useEffect(() => {
    const cargar = () => temporalesHoy().then(setTemporales).catch(() => {});
    cargar();
    const t = setInterval(cargar, 60000);
    return () => clearInterval(t);
  }, []);

  const COINCIDE = {
    nombre: L("nombre", "name"), cedula: L("cédula", "ID"), placa: L("placa", "plate"),
    temporal: L("visita autorizada", "authorized visit"),
  };

  useEffect(() => {
    const id = ++pedido.current;
    const q = texto.trim();
    if (!q) {
      setCasas([]); setTotal(0); setHayMas(false); setError(""); setCargando(false);
      return undefined;
    }
    setCargando(true);
    const t = setTimeout(() => {
      buscarCasas(q)
        .then((d) => {
          if (id !== pedido.current) return;
          setCasas(d.resultados || []);
          setTotal(d.total || 0);
          setHayMas(!!d.hay_mas);
          setError("");
        })
        .catch((e) => id === pedido.current && setError(e.message))
        .finally(() => id === pedido.current && setCargando(false));
    }, 280);
    return () => clearTimeout(t);
  }, [texto]);

  function ingresoDe(casa, p) {
    navigate("/garita/registrar", {
      state: {
        casa: casa.id,
        categoria: p.categoria,
        persona: p.persona_id,
        identificacion: p.cedula,
        nombre: p.nombre,
        placa: p.placas[0]?.placa || "",
      },
    });
  }

  function ingresoTemporal(a) {
    navigate("/garita/registrar", {
      state: {
        casa: a.filial_id,
        autorizacion: a.id,
        autorizado_por: a.autorizado_por,
        identificacion: a.identificacion,
        nombre: a.nombre,
        placa: a.placa,
        motivo: a.motivo,
        persona_visitada: a.autorizado_por,
      },
    });
  }

  // La relación de un autorizado (Familiar, Inquilino, Empleado) o su categoría.
  const relacion = (p) => textoDe(p.categoria === "AUTORIZADO" ? p.relacion : p.categoria);

  return (
    <>
      <div className="gar-buscar" style={{ marginBottom: 12 }}>
        <i className="fa-solid fa-magnifying-glass" />
        <input
          className="gar-input"
          type="search"
          inputMode="search"
          autoFocus
          placeholder={L("Casa, nombre, cédula o placa", "Unit, name, ID or plate")}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          aria-label={L("Buscar", "Search")}
        />
      </div>

      {error && <div className="gar-aviso error"><i className="fa-solid fa-triangle-exclamation" />{error}</div>}

      {!texto.trim() && temporales.length > 0 && (
        <div className="gar-card" style={{ marginBottom: 10 }}>
          <div className="gar-fila" style={{ justifyContent: "space-between", marginBottom: 2 }}>
            <strong style={{ fontSize: 16 }}>
              <i className="fa-solid fa-calendar-check" style={{ color: "var(--gar-acento)", marginRight: 8 }} />
              {L("Autorizados de hoy", "Authorized for today")}
            </strong>
            <span className="gar-tag marca">{temporales.length}</span>
          </div>
          <div style={{ color: "var(--gar-suave)", fontSize: 13, marginBottom: 4 }}>
            {L("Visitas que los residentes dejaron autorizadas desde su cuenta.",
               "Visitors that residents authorized from their account.")}
          </div>
          {temporales.map((a) => (
            <TemporalFila key={a.id} a={a} conCasa onIngreso={() => ingresoTemporal(a)} />
          ))}
        </div>
      )}

      {!texto.trim() && (
        <div className="gar-card gar-vacio">
          <i className="fa-solid fa-magnifying-glass" style={{ fontSize: 26, marginBottom: 10, display: "block", color: "var(--gar-acento)" }} />
          <strong style={{ display: "block", color: "var(--gar-texto)", fontSize: 16, marginBottom: 4 }}>
            {L("¿A quién buscás?", "Who are you looking for?")}
          </strong>
          {L("Escribí el número de casa, el nombre, la cédula o la placa.",
             "Type the unit number, the name, the ID or the plate.")}
          <div className="gar-fila" style={{ justifyContent: "center", marginTop: 16 }}>
            <button type="button" className="gar-btn gar-btn-primario" onClick={() => navigate("/garita/registrar")}>
              <i className="fa-solid fa-circle-plus" /> {L("Registrar visita", "Register visitor")}
            </button>
            <button type="button" className="gar-btn gar-btn-sec" onClick={() => navigate("/garita/adentro")}>
              <i className="fa-solid fa-people-arrows" /> {L("Adentro", "Inside")} ({adentro})
            </button>
          </div>
        </div>
      )}

      {texto.trim() && cargando && casas.length === 0 && (
        <div className="gar-vacio"><i className="fa-solid fa-circle-notch fa-spin" /> {L("Buscando…", "Searching…")}</div>
      )}

      {texto.trim() && hayMas && (
        <div className="gar-aviso info" style={{ marginBottom: 10 }}>
          <i className="fa-solid fa-filter" />
          <span>{L(`Se muestran ${casas.length} de ${total} casas. Escribí más para afinar la búsqueda.`,
                   `Showing ${casas.length} of ${total} units. Type more to narrow the search.`)}</span>
        </div>
      )}

      {texto.trim() && !error && !cargando && casas.length === 0 && (
        <div className="gar-card gar-vacio">
          <i className="fa-solid fa-house-circle-xmark" style={{ fontSize: 26, marginBottom: 8, display: "block" }} />
          {L(`No hay ninguna casa que coincida con "${texto}".`, `No unit matches "${texto}".`)}
          <div style={{ marginTop: 14 }}>
            <button type="button" className="gar-btn gar-btn-sec"
              onClick={() => navigate("/garita/registrar", { state: { identificacion: texto.trim() } })}>
              <i className="fa-solid fa-circle-plus" /> {L("Registrar una visita", "Register a visitor")}
            </button>
          </div>
        </div>
      )}

      {casas.map((casa) => (
        <div key={casa.id} className="gar-card">
          <div className="gar-fila" style={{ justifyContent: "space-between", marginBottom: 4 }}>
            <div className="gar-fila" style={{ gap: 10 }}>
              <span style={{ fontSize: 22, fontWeight: 900 }}>{casa.idFilial}</span>
              {casa.tipo_propiedad && <span className="gar-tag">{casa.tipo_propiedad}</span>}
              {texto && COINCIDE[casa.coincide] && (
                <span className="gar-tag azul">{L("Coincide por", "Matches by")} {COINCIDE[casa.coincide]}</span>
              )}
            </div>
            <button type="button" className="gar-btn gar-btn-sec gar-btn-chico"
              onClick={() => navigate("/garita/registrar", { state: { casa: casa.id } })}>
              <i className="fa-solid fa-user-plus" /> {L("Visita", "Visitor")}
            </button>
          </div>

          {casa.personas.length === 0 && (
            <div style={{ color: "var(--gar-suave)", padding: "8px 0" }}>
              {L("Esta casa no tiene personas registradas.", "No people registered in this unit.")}
            </div>
          )}
          {(casa.temporales || []).map((a) => (
            <TemporalFila key={`t-${a.id}`} a={a} onIngreso={() => ingresoTemporal(a)} />
          ))}
          {casa.personas.map((p, i) => (
            <div key={`${p.categoria}-${p.persona_id}-${i}`} className="gar-persona">
              <div className="gar-avatar"><i className={`fa-solid ${ICONO[p.categoria]}`} /></div>
              <div className="gar-persona-info">
                <strong>{p.nombre}</strong>
                <div className="gar-fila" style={{ gap: 6, marginTop: 3 }}>
                  <span className={`gar-tag ${p.categoria === "AUTORIZADO" ? "azul" : "marca"}`}>{relacion(p)}</span>
                  <small>{p.cedula}</small>
                  {p.placas.map((v) => (
                    <span key={v.placa} className="gar-placa"
                      title={v.extranjera ? L("Placa extranjera", "Foreign plate") : ""}>
                      {v.placa}{v.extranjera ? " *" : ""}
                    </span>
                  ))}
                </div>
                {p.telefonos.length > 0 && (
                  <div className="gar-fila" style={{ gap: 6, marginTop: 6 }}>
                    {p.telefonos.map((t) => (
                      <a key={t} href={`tel:${t.replace(/[^\d+]/g, "")}`} className="gar-tag"
                        style={{ textDecoration: "none", padding: "5px 10px", fontSize: 13 }}>
                        <i className="fa-solid fa-phone" /> {t}
                      </a>
                    ))}
                  </div>
                )}
              </div>
              <button type="button" className="gar-btn gar-btn-primario gar-btn-chico"
                onClick={() => ingresoDe(casa, p)} title={L("Registrar su ingreso", "Register entry")}>
                <i className="fa-solid fa-right-to-bracket" /> {L("Ingreso", "Entry")}
              </button>
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

/**
 * Una visita autorizada por el residente para hoy. Dice quién la autorizó y
 * si ya entró; "Ingreso" abre Registrar con todo lleno.
 */
function TemporalFila({ a, conCasa = false, onIngreso }) {
  const L = useLang();
  const hasta = new Date(`${a.hasta}T00:00:00`).toLocaleDateString(L("es-CR", "en-US"), { day: "2-digit", month: "2-digit" });
  const soloHoy = a.desde === a.hasta;
  return (
    <div className="gar-persona">
      <div className="gar-avatar" style={{ background: "var(--gar-ok-bg)", color: "var(--gar-ok)" }}>
        <i className="fa-solid fa-calendar-check" />
      </div>
      <div className="gar-persona-info">
        <strong>{conCasa && <span style={{ color: "var(--gar-acento)" }}>{a.filial} · </span>}{a.nombre}</strong>
        <div className="gar-fila" style={{ gap: 6, marginTop: 3 }}>
          {/* En "Autorizados de hoy" el título ya lo dice; dentro de una casa la distingue de su gente. */}
          {!conCasa && <span className="gar-tag verde">{L("Visita autorizada", "Authorized visit")}</span>}
          <span className="gar-tag">{textoMotivo(a.motivo)}</span>
          {a.identificacion && <small>{a.identificacion}</small>}
          {a.placa && <span className="gar-placa">{a.placa}</span>}
          {!soloHoy && <small>{L(`hasta el ${hasta}`, `until ${hasta}`)}</small>}
        </div>
        <div style={{ color: "var(--gar-suave)", fontSize: 13, marginTop: 4 }}>
          {a.autorizado_por && <>{L("Autorizó:", "Authorized by:")} {a.autorizado_por}</>}
          {a.notas && <> · <i className="fa-solid fa-note-sticky" /> {a.notas}</>}
        </div>
        {a.adentro ? (
          <span className="gar-tag azul" style={{ marginTop: 6 }}>{L("Está adentro", "Inside now")}</span>
        ) : a.ingresos_hoy > 0 ? (
          <span className="gar-tag" style={{ marginTop: 6 }}>{L("Ya entró hoy", "Already came in today")}</span>
        ) : null}
      </div>
      {!a.adentro && (
        <button type="button" className="gar-btn gar-btn-primario gar-btn-chico" onClick={onIngreso}
          title={L("Registrar su ingreso", "Register entry")}>
          <i className="fa-solid fa-right-to-bracket" /> {L("Ingreso", "Entry")}
        </button>
      )}
    </div>
  );
}
